import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "@ca-dmv-radv/translation";
import { Button, ButtonWrapper } from "@ca-dmv/core";
import { RadvPageWrapper, ButtonSaveAndExit } from "@ca-dmv-radv/components";
import { isMobile } from "react-device-detect";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  getPaymentDetails,
  postPayment,
  useSMS,
  SMS_SENDING,
  useApplication,
  useDocumentUploads,
} from "@ca-dmv-radv/data";

import IntlPhoneNumber from "../dashboard/Document/IntlPhoneNumber";
import TakePhotoWaiting from "../dashboard/Document/TakePhotoWaiting";

import {
  PAYMENT_STATUS_COMPLETED,
  PAYMENT_STATUS_INITIATED,
} from "../constants";

import { generateUrl } from "../dashboard/PresenceVerification/constants";

const APPLICATION_TYPE = "rnc";
const PAYMENT_ALREADY_MADE = "PAYMENT_ALREADY_MADE";

export default function RNCPaymentLanding({ title }) {
  const { t } = useTranslation();
  const formProps = { isForm: false };

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [feeAmount, setFeeAmount] = useState(0);
  const [skipDMV, setSkipDMV] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentDetailsLoading, setIsPaymentDetailsLoading] = useState(true);

  const { application } = useApplication();
  const { documentUploads } = useDocumentUploads();

  const {
    phoneNumber,
    sendingSMS,
    hasSentSMS,
    setPhoneNumber,
    setDocumentId,
    setShouldSendSMS,
  } = useSMS();

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const response = await getPaymentDetails(APPLICATION_TYPE);

      if (response && response.success && response.data) {
        const {
          skipDMV: skip,
          paymentStatus: status,
          feeAmount: fee,
        } = response.data;

        setSkipDMV(skip || false);
        setPaymentStatus(status || null);
        setFeeAmount(fee != null ? fee : 0);
      }
    } catch {
      setSkipDMV(false);
      setPaymentStatus(null);
      setFeeAmount(0);
      return undefined;
    } finally {
      setIsPaymentDetailsLoading(false);
    }

    return undefined;
  }, []);

  useEffect(() => {
    fetchPaymentDetails();
  }, [fetchPaymentDetails]);

  useEffect(() => {
    if (paymentStatus?.toUpperCase() === PAYMENT_STATUS_INITIATED) {
      const intervalId = setInterval(fetchPaymentDetails, 10000);
      return () => clearInterval(intervalId);
    }

    return undefined;
  }, [paymentStatus, fetchPaymentDetails]);

  const goToApplication = useCallback(() => {
    const url = generateUrl(application, documentUploads);

    if (url) {
      window.location.assign(`/rnc${url}`);
    }
  }, [application, documentUploads]);

  const initiatePaymentWorkflow = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await postPayment(APPLICATION_TYPE);

      if (!response?.success || !response?.data) {
        setIsLoading(false);
        return;
      }

      if (response.data.code === PAYMENT_ALREADY_MADE) {
        goToApplication();
        return;
      }

      window.location.assign(response.data);
    } catch {
      setIsLoading(false);
    }
  }, [goToApplication]);

  // Determine whether to hide Pay Now
  const hidePayNow =
    !skipDMV ||
    paymentStatus?.toUpperCase() === PAYMENT_STATUS_COMPLETED ||
    feeAmount <= 0;

  const pageHeading = () => {
    if (!isMobile) {
      return null;
    }

    return t("document-upload-checlist", "Document Upload Checklist");
  };

  return (
    <RadvPageWrapper
      fullWidth
      formProps={formProps}
      showRequiredFieldSeparator={false}
      pageTitle={title}
      pageHeading={pageHeading()}
    >
      <div id="nch-landing-div" className="container container--full">
        {!isMobile && skipDMV ? (
          <div className="flex flex--col flex--align-center mt-40">
            <h2 className="h3 max-width--800 text--xmd bp-sm:text--med bp-md:text--xlg text--700 text--center mb-24 bp-md:mb-48">
              {t(
                "screens-error-mobile-device-heading",
                "Please use a mobile device to proceed"
              )}
            </h2>

            <p className="text--sm mb-24 text--center max-width--600">
              {t(
                "nch-landing-not-mobile-message",
                "Please enter your mobile phone number to receive a secure link to continue on your mobile device."
              )}
            </p>

            <form
              className="flex flex--col flex--align-center w--100 max-width--400"
              onSubmit={(e) => {
                e.preventDefault();
                setDocumentId(0);
                setShouldSendSMS(true);
              }}
            >
              <div className="w--100 mb-20">
                <IntlPhoneNumber
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                />
              </div>

              <Button
                label={`${t("nch-send-link", "Send Link")} ${
                  sendingSMS === SMS_SENDING ? "..." : ""
                }`}
                disabled={
                  sendingSMS === SMS_SENDING ||
                  phoneNumber === undefined ||
                  !isValidPhoneNumber(phoneNumber)
                }
                isSubmit
              />
            </form>

            {hasSentSMS && (
              <TakePhotoWaiting onClose={() => {}} isLandingPage />
            )}
          </div>
        ) : (
          <>
            <ol className="nch-landing-large-list-block__list">
              <li className="nch-landing-large-list-block__list-item">
                <span className="list-text">
                  {t(
                    "nch-landing-gather-the-required-proof-documents-to-show-dmv",
                    "Gather the required proof documents to show DMV."
                  )}

                  <a
                    className="pdf-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.dmv.ca.gov/portal/file/federal-non-compliant-dl-id-card-documents-list-pdf/"
                  >
                    {" "}
                    {t(
                      "nch-landing-see-acceptable-list-of-documents-pdf",
                      "See acceptable documents list (PDF)"
                    )}
                  </a>
                </span>
              </li>

              {!hidePayNow && (
                <li className="nch-landing-large-list-block__list-item">
                  {t(
                    "nch-landing-select-the-paynow-button-to-continue-with-your-application",
                    "Select the 'Pay Now' button to continue with your application."
                  )}
                </li>
              )}
            </ol>

            <ButtonWrapper wrapperClass="rnc-landing-actions mt-60 bp-md:mt-60">
              <div className="rnc-landing-action">
                {isPaymentDetailsLoading ? (
                  <Button
                    label={t("nch-loading", "Loading...")}
                    isLoading
                    disabled
                  />
                ) : hidePayNow ? (
                  <Button
                    label={t("nch-continue", "Continue")}
                    onClick={goToApplication}
                  />
                ) : (
                  <Button
                    label={t("nch.payNow", "Pay Now")}
                    isLoading={isLoading}
                    disabled={isLoading}
                    onClick={initiatePaymentWorkflow}
                  />
                )}
              </div>

              <div className="rnc-landing-action">
                <ButtonSaveAndExit ignoreSendEmail />
              </div>
            </ButtonWrapper>
          </>
        )}
      </div>
    </RadvPageWrapper>
  );
}
